import type { DevicePreference } from '../data/settings.ts';
import type { SpotifyDevice } from './types.ts';

export type DeviceRole = 'primary' | 'fallback' | 'active';

export interface ResolvedDevice {
  device: SpotifyDevice;
  role: DeviceRole;
}

export function toPreference(device: SpotifyDevice): DevicePreference {
  return { name: device.name, id: device.id, type: device.type };
}

function matches(device: SpotifyDevice, preference: DevicePreference | null): boolean {
  if (!preference) return false;
  if (preference.id && device.id === preference.id) return true;
  return device.name.trim().toLowerCase() === preference.name.trim().toLowerCase();
}

/**
 * Waehlt das Zielgeraet nach fester Rangfolge:
 *
 * 1. Wunschgeraet (das iPad, auf dem Spotibox laeuft)
 * 2. Ersatzgeraet (z. B. Alexa im Kinderzimmer)
 * 3. irgendein gerade aktives Geraet
 *
 * Gibt `null` zurueck, wenn nichts steuerbar ist – dann zeigt die App den
 * „Hol einen Erwachsenen"-Bildschirm.
 */
export function resolveTargetDevice(
  devices: SpotifyDevice[],
  primary: DevicePreference | null,
  fallback: DevicePreference | null,
): ResolvedDevice | null {
  // Geraete ohne ID oder mit Steuerungssperre sind per Web API nicht bedienbar.
  const usable = devices.filter((device) => device.id && !device.is_restricted);

  const primaryMatch = usable.find((device) => matches(device, primary));
  if (primaryMatch) return { device: primaryMatch, role: 'primary' };

  const fallbackMatch = usable.find((device) => matches(device, fallback));
  if (fallbackMatch) return { device: fallbackMatch, role: 'fallback' };

  const active = usable.find((device) => device.is_active);
  if (active) return { device: active, role: 'active' };

  return null;
}
